import { siteId as defaultSiteId } from 'src/config'
import Model from 'src/models/model'
import { prependWithConsumerRoot } from 'src/router/utils'

/** @typedef {import('vue-router').NavigationGuard} NavigationGuard */

/**
 * Immediately clear any prior previewed permissions when loading the app for
 * a user who should not be allowed to preview permissions. This is to prevent
 * accidental user access to non-permitted UI elements.
 * @type {NavigationGuard}
 */
export async function checkPreviewPermissionsAccess (_to, _from, next) {
  await Model.ensureBooted()
  if (Model.context && !Model.context.isPreviewPermissionsPermitted) {
    Model.context.stopPreviewingPermissions()
  }

  next()
}

const switchSite = async (toSiteId) => {
  const previousSiteId = Model.configuration.currentSite.id
  if (Model.context.currentUser) {
    Model.context.currentUser.siteId = toSiteId
  }
  Model.context.clientReloadProgress = 0.01

  // These headers are set by Checkout route guards and will cause the API
  // to respond with the a site we might be switching away from.
  Model.app.$clearGlobalHeaders('showing-id', 'activity-id')
  const order = Model.app.config.globalProperties.$order
  if (order) {
    try {
      // We don't support multi-site orders
      order.abandon()
      order.reset()
    } catch (e) {
      console.error('Failed to abandon order', e)
      if (typeof Rollbar !== 'undefined') {
        // eslint-disable-next-line no-undef
        Rollbar.error('Failed to abandon order', e, { previousSiteId, toSiteId })
      }
    }
  }

  try {
    // Client config cache versions to force a cache miss during config validate().
    Model.configuration.version = null
    await Model.app.config.globalProperties.$handleAuthChange(Model.configuration.currentCircuit.id, toSiteId, true)
    Model.context.clientReloadProgress = 0.4
    await Model.context.changeCircuitOrSite()
    Model.context.clientReloadProgress = 0.6
  } catch (e) {
    console.error(e)
    if (typeof Rollbar !== 'undefined') {
      // eslint-disable-next-line no-undef
      Rollbar.error('Site switch failed', e, { previousSiteId, toSiteId })
    }
  }
}

/**
 * This beforeEnter route guard allows switching to the root site and path ('/') when
 * the 'primary=true' query param is present. The query param triggers a site switch
 * to the base "/" site, or the default site on app boot. The site switch is necessary
 * before the  'addMissingCurrentSitePathPrefix' guard so that it doesn't try to prepend
 * the path with a non-root site path. This should be the first site switching guard.
 * @type {NavigationGuard}
 */
export async function switchToRootPrimarySiteIfForced (to, _from, next) {
  // Guards in a sequence must share the same ensureBooted awaits
  // to ensure proper execution order.
  await Model.ensureBooted()
  const currentSite = Model.configuration.currentSite
  const sharedHostnameSiteIdsByPath = currentSite.sharedHostnameSiteIdsByPath
  const rootPathSiteId = sharedHostnameSiteIdsByPath['/'] || (currentSite.rootPath === '/' ? currentSite.id : parseInt(defaultSiteId))

  // For no-ops, the unaltered next() must be called to avoid a redirect which enters an infinite loop.
  if (to.query.primary === 'true' && _.endsWith(to.name, ':home-page') && currentSite.id === rootPathSiteId) {
    // The initial homepage navigation may append the query param when starting as the root site.
    // We want to keep the query param for browser history.
    next()
  } else if (to.query.primary === 'true' && currentSite.id !== rootPathSiteId) {
    await switchSite(rootPathSiteId)
    next({ path: to.path, query: _.omit(to.query, 'primary'), hash: to.hash })
  } else {
    next()
  }
}

/**
 * When supporting sharing URL domains across multiple sites via URL (sub)paths, this
 * beforeEnter guard should be set before the site switching guard.
 * Since most URL links up until this feature assumed a root path of '/', we need to
 * modify such URLs by prefixing them with the current site's root path.
 * Example assuming current site is "site-1":
 *   '/home' -> '/site-1/home'
 *   '/site-2/home' -> '/site-1/home'
 *
 * The only exception is when the navigation includes the query param 'primary=true',
 * in which case we assume we want to redirect and switch to the root '/' path and site,
 * removing non-root site prefix if present. Example:
 *   '/site-1/home?primary=true' -> '/home'
 *
 * This is expected to by followed by the `switchToRequiredSite` beforeEnter guard.
 * @type {NavigationGuard}
 */
export async function addMissingCurrentSitePathPrefix (to, _from, next) {
  await Model.ensureBooted()
  const currentSite = Model.configuration.currentSite
  if (currentSite.rootPath === '/') {
    next()
    return
  }

  // Note that the id is a string instead of a number
  const siteIdMatchingNonRootPath = _.findKey(currentSite.sharedHostnamePathMatchersBySiteId, (sitePathRegexp, _siteId) => {
    return sitePathRegexp.test(to.path)
  })
  if (siteIdMatchingNonRootPath) {
    // The path includes some site prefix. We don't want to alter the path regardless of whether
    // it matches the current site's path because the user may be trying to switch sites.
    next()
    return
  }

  let correctedPath
  if (currentSite.rootPath !== '/') {
    // The to.path does not have any site prefix, but by now we know the currentSite
    // requires one. Prepend the currentSite path prefix if required and missing.
    const requiredRootPath = prependWithConsumerRoot(currentSite.rootPath)
    if (to.path !== requiredRootPath && !_.startsWith(to.path, `${requiredRootPath}/`)) {
      correctedPath = to.path === '/' ? requiredRootPath : `${requiredRootPath}${to.path}`
    }
  }

  if (correctedPath) {
    next({ path: correctedPath, query: { ...to.query }, hash: to.hash })
  } else {
    next()
  }
}

/**
 * This is a beforeEnter route guard that can trigger switching the currentSite and
 * reloading all data when navigating to a (sub)path mapped to a specific site. The
 * target route's `meta.requiredSiteId` value is used to determine the site to switch to.
 * For example navigating from '/site-1/home' to '/site-2/home' would trigger a site switch
 * if not already switched.
 *
 * Any redirects to a different site's path should be performed before this guard.
 * @type {NavigationGuard}
 */
export async function switchToRequiredSite (to, _from, next) {
  await Model.ensureBooted()
  const currentSiteId = Model.configuration.currentSite.id
  const requiredSiteId = to.meta.requiredSiteId

  if (requiredSiteId && currentSiteId && requiredSiteId !== currentSiteId) {
    await switchSite(requiredSiteId)
  }
  next()
}

/**
 * This guard is intended to be used as a global beforeEach guard set in consumer mode
 * for SEO purposes, though it can also be added as a beforeEnter guard to individual routes.
 *
 * This guard adds, updates, or removes the `<link rel="canonical" href="${currentUrl}">` tag
 * based on whether the destination route has a `meta: { canonical: true }` field. When the
 * user navigates to another route without the meta field, the canonical link tag is removed.
 * @type {NavigationGuard}
 */
export async function addOrRemoveCanonicalLinkTag (to, _from, next) {
  await Model.ensureBooted()

  const isCanonicalRoute = to.meta.canonical
  if (isCanonicalRoute) {
    Model.app.config.globalProperties.$addCanonicalLinkTag(to.path)
  } else {
    Model.app.config.globalProperties.$removeCanonicalLinkTag()
  }

  next()
}
